import {
  Alert,
  Button,
  Collapse,
  ColorInput,
  Divider,
  Group,
  NumberInput,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconAlertTriangle, IconChevronUp, IconPencil, IconPlus, IconRefresh } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { failsAA } from '../../helpers/brandColors';
import HelpPopover from '../HelpPopover/HelpPopover';
import BrandKitSummary from './BrandKitSummary';
import { COLOR_ROLES, DARK_ROLES } from './brandIdentity';
import LogoField from './LogoField';
import ProvenanceLabel from './ProvenanceLabel';
import useLogoVanishesOnDark from './useLogoVanishesOnDark';

const HELP = {
  logoDark: {
    label: 'Logo for dark backgrounds',
    what: 'Used in dark-mode inboxes and on dark panels. Usually a white or light version of your logo.',
    examples: [{ label: 'A white logo on a transparent PNG' }],
  },
  darkMode: {
    label: 'Dark mode',
    what: 'The colors mail apps use when a reader has dark mode on. We work them out from your brand; change any you want.',
    examples: [{ label: 'Page #161616, text #F4F4F4, links in your primary color' }],
  },
  fontFamily: {
    label: 'Font',
    what: 'The font list for headings and text, best first. Many inboxes only show common fonts, so end with a safe fallback.',
    examples: [{ label: "'Inter', Arial, sans-serif" }, { label: "Georgia, 'Times New Roman', serif" }],
  },
  webFontUrl: {
    label: 'Web font link',
    what: 'A stylesheet link that loads your font, for the inboxes that can show web fonts.',
    examples: [{ label: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;700' }],
  },
  radius: {
    label: 'Corners',
    what: 'How round buttons, cards and images are, in pixels (0 to 48).',
    examples: [{ label: '0 for square' }, { label: '8 for slightly rounded' }, { label: '24 for pill buttons' }],
  },
};

const help = (key) => <HelpPopover {...HELP[key]} />;

// An input label with its "?" next to it.
const helpLabel = (key) => (
  <Group gap={4} component="span">
    {HELP[key].label} {help(key)}
  </Group>
);

const COLLAPSE_MS = 200;

const noRoleHandlers = () => ({});

// The brand kit: a read-only summary first, since most of it comes from the site and is right, and
// the full editor opened in place under it. The editor stays mounted while closed, so its inputs,
// refs and error keys ('colors.<role>', 'darkColors.<role>') are always there.
const BrandKitSection = ({
  visual,
  logoUrl,
  sources = {},
  previewColors,
  derivedDark: derived,
  darkPalette,
  fieldErrors = {},
  onVisualChange: changeVisual,
  onLogoChange: changeLogo,
  activeRole,
  roleHandlers = noRoleHandlers,
}) => {
  const colors = visual?.colors || {};
  const darkColors = visual?.darkColors || {};
  // What the client sees for each dark role: their own value, or the one worked out for them.
  const darkShown = Object.fromEntries(DARK_ROLES.map(({ key }) => [key, darkColors[key] || derived[key] || '']));
  const logoVanishes = useLogoVanishesOnDark(visual?.logoDarkUrl ? null : logoUrl);
  const isEmpty = !logoUrl && !visual?.logoDarkUrl && !Object.values(colors).some(Boolean);
  // Until the client opens or closes it, an empty kit (the read failed or was skipped, or has not
  // landed yet) shows the editor, since there is nothing to summarise.
  const [openChoice, setOpenChoice] = useState(null);
  const open = openChoice ?? isEmpty;
  // The first edit fills the draft and flips isEmpty, so pin the editor open rather than collapse it mid-edit.
  const onVisualChange = (patch) => {
    setOpenChoice((choice) => choice ?? true);
    changeVisual(patch);
  };
  const onLogoChange = (url) => {
    setOpenChoice((choice) => choice ?? true);
    changeLogo(url);
  };
  const [focusToken, setFocusToken] = useState(null);
  const inputs = useRef({});
  const inputRef = (token) => (el) => {
    inputs.current[token] = el;
  };

  // Every failed save brings a new errors object, so a second failure after Done reopens it too.
  useEffect(() => {
    if (Object.keys(fieldErrors).length) {
      setOpenChoice(true);
    }
  }, [fieldErrors]);

  // Focus once the editor is visible: it is display:none until the Collapse starts opening.
  useEffect(() => {
    if (!open || !focusToken) {
      return undefined;
    }
    const timer = setTimeout(() => {
      inputs.current[focusToken]?.focus();
      setFocusToken(null);
    }, COLLAPSE_MS + 20);
    return () => clearTimeout(timer);
  }, [open, focusToken]);

  const editRole = (token) => {
    setOpenChoice(true);
    setFocusToken(token);
  };

  const setColor = (key, value) => onVisualChange({ colors: { ...colors, [key]: value } });
  // Editing one dark role keeps the others where the client sees them (the derived value if blank).
  const setDarkColor = (key, value) => onVisualChange({ darkColors: { ...derived, ...darkColors, [key]: value } });

  const contrastIssues = [];
  if (failsAA(previewColors.text, previewColors.page)) {
    contrastIssues.push('Your text color is hard to read on the page color.');
  }
  if (failsAA('#FFFFFF', previewColors.primary)) {
    contrastIssues.push('White button text is hard to read on your primary color.');
  }

  let toggle = { label: 'Edit', ariaLabel: 'Edit brand kit', icon: <IconPencil size={14} /> };
  if (open) {
    toggle = { label: 'Done', ariaLabel: 'Close the brand kit editor', icon: <IconChevronUp size={14} /> };
  } else if (isEmpty) {
    toggle = { label: 'Add', ariaLabel: 'Add your brand kit', icon: <IconPlus size={14} /> };
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <div>
          <Title order={4} fw={600}>
            Brand kit
          </Title>
          <Text size="sm" c="dimmed">
            Your logos, colors and font, as emails and pop-ups use them.
          </Text>
        </div>
        <Button
          size="xs"
          variant="light"
          flex="none"
          leftSection={toggle.icon}
          aria-label={toggle.ariaLabel}
          aria-expanded={open}
          onClick={() => setOpenChoice(!open)}
        >
          {toggle.label}
        </Button>
      </Group>

      {!open && (
        <BrandKitSummary
          visual={visual}
          logoUrl={logoUrl}
          sources={sources}
          previewColors={previewColors}
          darkShown={darkShown}
          darkPalette={darkPalette}
          activeRole={activeRole}
          onEdit={editRole}
          roleHandlers={roleHandlers}
        />
      )}

      {logoVanishes && (
        <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} />}>
          Your logo is dark on a see-through background, so it may disappear in dark-mode inboxes. Add a logo for dark
          backgrounds.
        </Alert>
      )}

      {contrastIssues.length > 0 && (
        <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} />} data-testid="contrast-warning">
          {contrastIssues.map((issue) => (
            <div key={issue}>{issue}</div>
          ))}
        </Alert>
      )}

      <Collapse expanded={open} keepMounted keepMountedMode="display-none" transitionDuration={COLLAPSE_MS}>
        <Stack gap="lg">
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <LogoField
              label="Logo for light backgrounds"
              description="Your main logo, shown on white."
              background="#FFFFFF"
              value={logoUrl}
              onChange={onLogoChange}
              provenance={<ProvenanceLabel source={sources.logoUrl} />}
            />
            <LogoField
              label="Logo for dark backgrounds"
              description="Shown in dark-mode inboxes and dark panels."
              background="#1E1E1E"
              value={visual?.logoDarkUrl}
              onChange={(url) => onVisualChange({ logoDarkUrl: url })}
              help={help('logoDark')}
              provenance={<ProvenanceLabel source={sources.logoDarkUrl} />}
              error={fieldErrors.logoDarkUrl}
            />
          </SimpleGrid>

          <Divider />

          <div>
            <Text size="sm" fw={600} mb="xs">
              Colors
            </Text>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
              {COLOR_ROLES.map(({ key, label, hint }) => (
                <Stack key={key} gap={2}>
                  <ColorInput
                    ref={inputRef(`colors.${key}`)}
                    label={label}
                    description={hint}
                    format="hex"
                    placeholder={previewColors[key]}
                    value={colors[key] || ''}
                    onChange={(value) => setColor(key, value)}
                    error={fieldErrors[`colors.${key}`]}
                    {...roleHandlers(`colors.${key}`)}
                  />
                  <ProvenanceLabel source={sources[`colors.${key}`]} />
                </Stack>
              ))}
            </SimpleGrid>
          </div>

          <Divider />

          <div>
            <Group justify="space-between" mb="xs">
              <Group gap={4}>
                <Text size="sm" fw={600}>
                  Dark mode
                </Text>
                {help('darkMode')}
              </Group>
              <Button
                size="compact-sm"
                variant="light"
                leftSection={<IconRefresh size={14} />}
                onClick={() => onVisualChange({ darkColors: derived })}
              >
                Derive from brand
              </Button>
            </Group>
            <ProvenanceLabel source={sources.darkColors} />
            <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm" mt={4}>
              {DARK_ROLES.map(({ key, label, hint }) => (
                <ColorInput
                  key={key}
                  ref={inputRef(`darkColors.${key}`)}
                  label={label}
                  description={hint}
                  aria-label={`Dark mode: ${label}`}
                  format="hex"
                  value={darkShown[key]}
                  onChange={(value) => setDarkColor(key, value)}
                  error={fieldErrors[`darkColors.${key}`]}
                  {...roleHandlers(`darkColors.${key}`)}
                />
              ))}
            </SimpleGrid>
          </div>

          <Divider />

          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
            <Stack gap={2}>
              <TextInput
                label={helpLabel('fontFamily')}
                aria-label="Font"
                placeholder="Arial, sans-serif"
                value={visual?.fontFamily || ''}
                onChange={(e) => onVisualChange({ fontFamily: e.target.value })}
                error={fieldErrors.fontFamily}
              />
              <ProvenanceLabel source={sources.fontFamily} />
            </Stack>
            <Stack gap={2}>
              <TextInput
                label={helpLabel('webFontUrl')}
                aria-label="Web font link"
                placeholder="https://fonts.googleapis.com/css2?family=…"
                value={visual?.webFontUrl || ''}
                onChange={(e) => onVisualChange({ webFontUrl: e.target.value })}
                error={fieldErrors.webFontUrl}
              />
              <ProvenanceLabel source={sources.webFontUrl} />
            </Stack>
            <Stack gap={2}>
              <NumberInput
                label={helpLabel('radius')}
                aria-label="Corners"
                min={0}
                max={48}
                allowDecimal={false}
                allowNegative={false}
                suffix=" px"
                placeholder="8 px"
                value={visual?.radius ?? ''}
                onChange={(value) => onVisualChange({ radius: value === '' ? undefined : Number(value) })}
                error={fieldErrors.radius}
              />
              <ProvenanceLabel source={sources.radius} />
            </Stack>
          </SimpleGrid>
          {/* The toggle at the top is out of view by now. */}
          <Group justify="flex-end">
            <Button
              size="xs"
              variant="subtle"
              leftSection={<IconChevronUp size={14} />}
              onClick={() => setOpenChoice(false)}
            >
              Done
            </Button>
          </Group>
        </Stack>
      </Collapse>
    </Stack>
  );
};

export default BrandKitSection;
